import { useCallback, useContext, useEffect, useState } from "react"

//Kiosk components
import {
  kioskApiGetFormDependents,
  kioskApiPostFormRemove,
} from "../../kioskApi"

//PrimeReact
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { Dialog } from "primereact/dialog"
import { DataTable } from "primereact/datatable"
import { Dropdown } from "primereact/dropdown"
import { InputSwitch } from "primereact/inputswitch"

//SOAR Components
import { UserContext } from "../../../../context/userContext"
import { LogError } from "../../../../helpers/logger"
import { LoaderMedium } from "../../../../components/Loaders"

//Styles
import myStyles from "../../../../styles/kiosk/KioskAdminForm.module.css"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"

export default function DeleteKioskFormDialog({
  formId,
  forms,
  onClose,
  onSubmit,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formProfile, setFormProfile] = useState(undefined)

  const availableForms = forms?.filter((form) => form.id !== formId) || []

  const loadDependents = useCallback(async () => {
    try {
      const results = await kioskApiGetFormDependents(
        userCtx.tokenKiosk,
        formId
      )

      if (results?.firstTimeVisitors) {
        results.firstTimeVisitors = results.firstTimeVisitors.map((option) => {
          return { ...option, remove: true, newForm: undefined }
        })
      }

      setFormProfile(results)
      setIsLoading(false)
    } catch (error) {
      LogError("Unable to load Kiosk form dependents", error)
      userCtx.setNotificationError("Unable to load Kiosk form dependents")
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId])

  useEffect(() => {
    loadDependents()
  }, [loadDependents])

  const submitDeletion = async () => {
    try {
      setIsSubmitting(true)

      const payload = {
        id: formId,
        userId: userCtx.session.user.staff_id,
        firstTimeVisitorOptionUpdates: formProfile.firstTimeVisitors.map(
          (option) => {
            return {
              firstTimeVisitorOptionId: option.id,
              formId: option.newForm,
            }
          }
        ),
      }

      await kioskApiPostFormRemove(userCtx.tokenKiosk, payload)

      onSubmit()
    } catch (error) {
      LogError("Unable to delete Kiosk form", error)
      userCtx.setNotificationError("Unable to delete Kiosk form")
      setIsSubmitting(false)
    }
  }

  const NewFormColumnTemplate = (rowData) => {
    return (
      <Dropdown
        options={availableForms}
        optionLabel="name"
        optionValue="id"
        filter
        value={rowData.newForm}
        placeholder={
          rowData.remove ? "To be removed" : "Select a replacement form"
        }
        onChange={(e) => {
          let dataSet = { ...formProfile }
          const index = dataSet.firstTimeVisitors.indexOf(rowData)
          dataSet.firstTimeVisitors[index] = { ...rowData, newForm: e.value }

          setFormProfile(dataSet)
        }}
        disabled={rowData.remove}
        className={myStyles.deleteFormNewFormDropdown}
      />
    )
  }

  const RemoveColumnTemplate = (rowData) => {
    return (
      <div className={kioskStyles.switchControl}>
        <InputSwitch
          checked={rowData.remove}
          onChange={(e) => {
            let dataSet = { ...formProfile }
            const index = dataSet.firstTimeVisitors.indexOf(rowData)
            dataSet.firstTimeVisitors[index] = {
              ...rowData,
              remove: e.value,
              newForm: undefined,
            }

            setFormProfile(dataSet)
          }}
          className={myStyles.deleteFormNewFormSwitch}
        />
      </div>
    )
  }

  const disableDelete = () => {
    let allowDelete = !isLoading && formProfile && !isSubmitting

    if (allowDelete && formProfile?.firstTimeVisitors) {
      allowDelete = !formProfile.firstTimeVisitors.some(
        (option) => !option.remove && !option.newForm
      )
    }

    return !allowDelete
  }

  return (
    <Dialog
      header={"Delete kiosk Form"}
      visible={true}
      onHide={onClose}
      className={myStyles.deleteFormDialog}
    >
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <>
          {formProfile?.firstTimeVisitors &&
          formProfile?.firstTimeVisitors?.length > 0 ? (
            <>
              <div
                className={`${myStyles.deleteFormTitle} ${myStyles.deleteFormWarning}`}
              >
                Please review the linked First Time Visitor options and
                re-assign those that should be kept to a new form.
              </div>
              <DataTable
                className={`${kioskStyles.dataTable} ${myStyles.deleteFormTable}`}
                value={formProfile?.firstTimeVisitors}
              >
                <Column header="Name" field="name" />
                <Column
                  header="New form"
                  body={NewFormColumnTemplate}
                  className={myStyles.deleteFormNewFormColumn}
                />
                <Column
                  header="Remove"
                  body={RemoveColumnTemplate}
                  className={myStyles.deleteFormRemoveColumn}
                />
              </DataTable>
            </>
          ) : (
            <div className={myStyles.deleteFormTitle}>
              Are you sure that you want to delete this form?
            </div>
          )}
          <div className={myStyles.deleteFormButtonContainer}>
            <Button
              label="Cancel"
              type="button"
              onClick={onClose}
              className={`${kioskStyles.invertButton} ${kioskStyles.button}`}
            />
            <Button
              label="Confirm delete"
              className={`${kioskStyles.button}`}
              disabled={disableDelete()}
              onClick={() => {
                submitDeletion()
              }}
              isSubmitting={isSubmitting}
            />
          </div>
        </>
      )}
    </Dialog>
  )
}
